import { ICommandBarItemProps, Text } from '@fluentui/react';
import { RouterParams } from 'App';
import { RootState } from 'app/store';
import CustomPanel from 'components/controls/CustomPanel';
import { EViewType, styleSummary } from 'global/Application';
import { BuildVisibilityScope } from 'global/Visibility/GlobalVisibility';
import { IComponentContextMenu } from 'model/Application';
import * as FAM from 'model/FlexibleAttributeModel';
import { useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useBoolean } from 'react-use';
import BlueRelationship from '.';
import { Constants } from '../../global/Application/Constants';
import BenefitProperties from './BenefitProperties';
import { default as BulkCostShareWizard, TCostshareWizardVariant } from './BulkCostShareWizard';
import PlanPanel from './Controls/PlanPanel';
import FlexibleFieldView, { EScopeType } from './FlexibleFieldView';
import NonStandardApplyTemplate from './NonStandardApplyTemplate';
import BenefitSummary from './Benefit/BenefitSummary';

/*
* Display Index:
* Group configuration
* - BlueRelationship
* - Summary + fields
* - Plans
* - Benefits
* - Group Structure
* - Fees
* Plan
* - BlueRelationship
* Benefit <-
* - BlueRelationship
*/

interface IProps extends TComponentConnectedProps, IComponentContextMenu {
    benefit?: FAM.IBenefit;

    plans?: FAM.IPlan[];
}



const Benefit = (props: IProps) => {
    const disabled = props.state.status.viewType !== EViewType.Modify;

    const routeParams = useParams<RouterParams>();

    const [propertiesIsOpen, setPropertiesIsOpen] = useState(false);
    const [costshareVariant, setCostshareVariant] = useState<TCostshareWizardVariant | null>(null);

    const [applyTemplateIsMounted, setApplyTemplateIsMounted] = useState(false);
    const [applyTemplateIsOpen, _setApplyTemplateIsOpen] = useState(false);
    const setApplyTemplateIsOpen: React.Dispatch<React.SetStateAction<boolean>> = (value) => {
        if (!applyTemplateIsMounted)
            setApplyTemplateIsMounted(true);
        _setApplyTemplateIsOpen(value);
    }
    const currentBenefit = props?.state?.data.json?.benefits?.find(b => b.id === routeParams.benefitId)

    const medicalCategory = props.state.data.config.cache?.map_bcbs_config_category_named.Medical;

    const benefit = props.benefit
        ? props.benefit
        : props.state?.data.json?.benefits?.find(q => q.id === routeParams.benefitId);

    const plans = props.plans
        ? props.plans
        : props.state.data.json.plans;


    useEffect(() => {
        document.title = `Benefit | ${benefit?.name ?? '-'} (${benefit?.bcbs_name ?? '-'})`
    }, [benefit]);

    const contextMenu = useMemo(() => {
        const items: ICommandBarItemProps[] = [];

        // Properties Menu Option
        if (!disabled) {
            items.push({
                key: 'benefitProperties',
                text: 'Properties',
                iconProps: { iconName: 'Settings' },
                onClick: () => setPropertiesIsOpen(true)
            });
            items.push({
                key: 'groupConfiguration.nonStandardTemplate.add',
                name: 'Apply Non-Standard Template',
                iconProps: { iconName: 'Generate' },
                onClick: (event, item) => {
                    setApplyTemplateIsOpen(true);
                },
            },);
        }

        // Bulk Costshare Menu Option
        if (!disabled)
            if (currentBenefit?.bcbs_config_categoryid === medicalCategory.bcbs_config_categoryid)
                items.push({
                    key: 'bulkCostshare',
                    text: 'Costshare',
                    iconProps: { iconName: 'Money' },
                    subMenuProps: {
                        items: [
                            {
                                key: 'costshareDefault',
                                text: 'Setup Defaults',
                                iconProps: { iconName: 'FavoriteStar' },
                                onClick: () => { setCostshareVariant('defaults') },
                            },
                            {
                                key: 'costshareSearchAndReplace',
                                text: 'Search and Replace',
                                iconProps: { iconName: 'Search' },
                                onClick: () => { setCostshareVariant('searchAndReplace') },
                            }
                        ]
                    }
                });

        return { items: items, key: 'plan' };
    }, [benefit, props.state.status])

    const controlVisibility = BuildVisibilityScope(
        null,
        props.state.data.json,
        benefit,
        null);

    const onRenderPlans = () => {
        if (!benefit)
            return <></>;

        return <PlanPanel plansBenefit={benefit} />;
    }

    const onRenderSummary = () => {
        if (!benefit)
            return <></>;

        return <CustomPanel title={"Summary"}
            canCollapse={true}
            defaultCollapsed={true}
            storageId={"benefit.summary"}
        >
            <BenefitSummary benefit={benefit} />
        </CustomPanel>;
    }

    const onRenderAttributes = () => {
        if (!benefit)
            return <></>;

        return <CustomPanel title={"Attributes"}
            canCollapse={true}
            defaultCollapsed={false}
            storageId={"benefit.attributes"}
        >
            <FlexibleFieldView
                fields={benefit.fields}
                category={benefit}
                tiers={benefit.tiers}
                scope={benefit}
                benefit={benefit}
                disabled={disabled}
                visibility={controlVisibility}
                cancellation={benefit.status?.value === FAM.EBenefitStatus.Cancelled}
                product={benefit?.product?.value === FAM.EProduct.Yes}
                scopeType={EScopeType.benefit}
            />
        </CustomPanel>;
    }

    const onRenderPanels = () => {
        return <>
            <BulkCostShareWizard
                isOpen={costshareVariant !== null}
                onDismiss={() => { setCostshareVariant(null) }}
                onCancel={() => { setCostshareVariant(null) }}
                variant={costshareVariant ?? 'defaults'}
                currentBenefit={currentBenefit}
            />

            {benefit &&
                <BenefitProperties
                    isOpen={propertiesIsOpen}
                    onDismiss={() => setPropertiesIsOpen(false)}
                    data={{ benefit: benefit }}
                />
            }
        </>;
    }

    const onRenderApplyTemplate = () => {
        if (!applyTemplateIsMounted)
            return <></>

        if (Constants.env !== 'Production' || Constants.app.isLocalHost) {

            return <>
                <NonStandardApplyTemplate
                    isOpen={applyTemplateIsOpen}
                    onDismiss={() => { setApplyTemplateIsOpen(false); setApplyTemplateIsMounted(false) }}
                    benefit={benefit}
                />
            </>;
        } else {
            return <></>
        }
    }

    return <BlueRelationship contextMenu={contextMenu}>
        {onRenderApplyTemplate()}
        {onRenderPanels()}
        {onRenderSummary()}
        {onRenderPlans()}
        {onRenderAttributes()}
    </BlueRelationship >;

}

function mapStateToProps(state: RootState) { return { state } }
const connector = connect(mapStateToProps);
type TComponentConnectedProps = ConnectedProps<typeof connector>;
export default connector(Benefit)

